/* Home-only progressive enhancement. Existing images and category links work without JS. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const hover = matchMedia('(hover: hover)');
  const initialize = (root = document) => {
    const data = root.querySelector('[data-home-motion-images]');
    if (!data) return;
    let images;
    try { images = JSON.parse(data.textContent); } catch { return; }
    root.querySelectorAll('[data-home-motion]').forEach(stage => {
      if (stage.dataset.motionReady) return;
      stage.dataset.motionReady = 'true';
      const hero = stage.dataset.homeMotion === 'all';
      const pool = images.filter(image => hero || image.category === stage.dataset.homeMotion);
      const base = stage.querySelector('img');
      if (!base || pool.length < 2) return;
      const overlay = document.createElement('img');
      overlay.className = 'vovi-motion-overlay';
      overlay.alt = '';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.decoding = 'async';
      stage.append(overlay);
      const link = hero ? stage.querySelector('[data-hero-motif-link]') : stage.closest('a');
      const setLink = image => { if (link && image?.url) { link.href = image.url; link.setAttribute('aria-label', image.alt.replace('Wohnbeispiel: ', '') + ' gestalten'); } };
      let visible = false, hovered = false, focused = false, paused = false;
      let loaded = document.readyState === 'complete';
      const control = hero ? stage.closest('figure')?.querySelector('[data-motion-toggle]') : null;
      let timer, busy = false, disposed = false, incoming;
      let current = pool.find(image => new URL(image.src, location.href).pathname === new URL(base.src).pathname);
      // Keep the server-rendered first image; avoid an extra hero download on load.
      setLink(current);
      let used = new Set(current ? [current.src] : []);
      const active = () => !disposed && loaded && !paused && visible && !document.hidden && !reduced.matches && (hero || !hover.matches || hovered || focused);
      const choose = () => {
        let available = pool.filter(image => image.src !== current?.src && !used.has(image.src) && (!hero || image.category !== current?.category));
        // Finish the cycle even if its remaining images share the current category.
        if (!available.length) available = pool.filter(image => image.src !== current?.src && !used.has(image.src));
        if (!available.length) {
          used = new Set(current ? [current.src] : []);
          available = pool.filter(image => image.src !== current?.src && (!hero || image.category !== current?.category));
        }
        // New motifs appear first in each cycle; established motifs remain included.
        const newest = available.filter(image => image.priority > 1);
        const candidates = newest.length ? newest : available;
        return candidates[Math.floor(Math.random() * candidates.length)];
      };
      const schedule = (delay = hero ? 4000 : 2600) => {
        clearTimeout(timer);
        if (active() && !busy) timer = setTimeout(change, delay);
      };
      const change = async () => {
        if (!active() || busy) return;
        busy = true;
        const next = choose();
        if (!next) { busy = false; return; }
        const preload = new Image();
        preload.sizes = base.sizes; preload.srcset = next.srcset || ""; preload.src = next.src;
        try {
          try { await preload.decode(); } catch { if (!preload.complete || !preload.naturalWidth) throw Error(); }
          if (!active()) return;
          overlay.sizes = base.sizes; overlay.srcset = next.srcset || ""; overlay.src = next.src;
          if (overlay.decode) { try { await overlay.decode(); } catch {} }
          if (!active()) return;
          incoming = next;
          overlay.classList.add('is-visible');
          await new Promise(resolve => setTimeout(resolve, hero ? 1000 : 900));
          if (disposed) return;
          base.srcset = next.srcset || ""; base.src = next.src;
          base.alt = next.alt;
          if (base.decode) { try { await base.decode(); } catch {} }
          overlay.classList.remove('is-visible');
          current = next;
          incoming = null;
          setLink(current);
          used.add(next.src);
        } catch {
          incoming = null;
          setLink(current);
          overlay.classList.remove('is-visible');
        } finally {
          busy = false;
          // Four seconds between the starts of consecutive hero fades.
          schedule(hero ? 3000 : 1700);
        }
      };
      const sync = () => schedule(hero ? 4000 : 1500);
      if (control) {
        const pauseLabel = control.textContent.trim() || 'Bildwechsel pausieren';
        const resumeLabel = document.documentElement.lang.startsWith('en') ? 'Resume slideshow' : 'Bildwechsel fortsetzen';
        control.textContent = '';
        control.setAttribute('aria-label', pauseLabel);
        stage.append(control);
        control.hidden = false;
        control.addEventListener('click', () => {
          paused = !paused;
          control.setAttribute('aria-pressed', String(paused));
          control.setAttribute('aria-label', paused ? resumeLabel : pauseLabel);
          sync();
        });
      }
      const onLoad = () => { loaded = true; sync(); };
      if (!loaded) window.addEventListener('load', onLoad, { once: true });
      const selectVisible = () => setLink(incoming && Number.parseFloat(getComputedStyle(overlay).opacity) >= 0.5 ? incoming : current);
      link?.addEventListener('pointerdown', selectVisible);
      link?.addEventListener('click', selectVisible);
      link?.addEventListener('contextmenu', selectVisible);
      link?.addEventListener('pointerenter', event => {
        if (hover.matches && event.pointerType !== 'touch') { hovered = true; if (!hero) schedule(1500); }
      });
      link?.addEventListener('pointerleave', () => { hovered = false; schedule(); });
      link?.addEventListener('focusin', () => { focused = true; if (!hero) schedule(1500); });
      link?.addEventListener('focusout', event => {
        if (!link.contains(event.relatedTarget)) { focused = false; schedule(); }
      });
      const observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        sync();
      }, { threshold: 0.15 });
      observer.observe(stage);
      document.addEventListener('visibilitychange', sync);
      reduced.addEventListener('change', sync);
      hover.addEventListener('change', sync);
      window.addEventListener('pageshow', sync);
      const section = stage.closest('.shopify-section');
      section?.addEventListener('shopify:section:unload', () => {
        disposed = true;
        clearTimeout(timer);
        observer.disconnect();
        document.removeEventListener('visibilitychange', sync);
        reduced.removeEventListener('change', sync);
        hover.removeEventListener('change', sync);
        window.removeEventListener('pageshow', sync);
        window.removeEventListener('load', onLoad);
      }, { once: true });
      sync();
    });
  };
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();

